/**
 * SafetyCommonDashboard — the Common Safety Dashboard.
 *
 * One place to understand safety performance, built so a reader can go from a
 * headline number to the record that explains it and the action that closes it:
 *   KPI → trend → breakdown → drill-down → source record → action.
 *
 * Everything on it is computed from existing tenant data through the Common
 * Dashboard Framework (@/dashboard): the metric registry supplies the KPIs, and
 * every panel honours the same period, site and dimension filters. Nothing here
 * holds a KPI value, site name, department, category or cause — see
 * @/dashboard/presets/safetyCommon for the widget catalog.
 *
 * Access: HSE Manager and above (the same management tier as Safety Overview).
 * That is a UI gate only — every metric is independently RBAC-checked and
 * site-scoped server-side, and the drill-downs re-authorize on each request.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Info, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TabNavigation from '../../components/TabNavigation';
import { useSite } from '../../context/SiteContext';
import {
  CustomizableDashboard, FilterBar, SavedFilters, ExportMenu, DimensionFilters,
  StandardToggle, getStoredStandard, useTimeFilter, useDimensionFilters,
  onDashboardLoad,
} from '@/dashboard';
import {
  SAFETY_COMMON_DIMENSIONS, buildSafetyCommonCatalog, safetyCommonDefaultLayout,
  safetyCommonExportSpec,
} from '@/dashboard/presets/safetyCommon';

const DIMENSION_KEYS = SAFETY_COMMON_DIMENSIONS.map((d) => d.key);
const DASHBOARD_KEY = 'safety-common';

/**
 * What this dashboard cannot show, said plainly. A dashboard that quietly omits
 * a requested cut is worse than one that names the gap: the reader would
 * otherwise assume the filter simply found nothing.
 */
const DATA_NOTES = [
  'Business Unit is not offered as a filter: safety records are stamped with a site, and the metric layer scopes to one site at a time, so a business-unit rollup is not yet expressible.',
  'Employee vs Contractor is not offered: neither the incident record nor the injured-person record stores a worker class.',
  'Audit-based figures (Audit Compliance, Audits Completed, findings) are organisation-wide — audit records carry no site, so they do not narrow with the site filter. Each card says so on its info button.',
  'CAPA "Escalated" is not shown: the CAPA record has an extension (date and reason), not an escalation, so there is nothing to count.',
];

/**
 * Freshness indicator, subscribing to the load signal ITSELF rather than taking
 * it as a prop. Roughly twenty widgets announce a successful load; holding that
 * timestamp in the page's state would re-render the whole grid — every recharts
 * chart included — once per announcement. Keeping it here confines each update
 * to this one span.
 */
function LastUpdated() {
  const [at, setAt] = useState(null);
  useEffect(() => onDashboardLoad(setAt), []);

  if (!at) return <span className="text-xs text-gray-400">Loading…</span>;
  return (
    <span className="text-xs text-gray-500">
      Last updated{' '}
      <time dateTime={at.toISOString()} className="font-medium text-gray-700">
        {at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </time>
    </span>
  );
}

const SafetyCommonDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { activeSiteId } = useSite();

  const { preset, dateFrom, dateTo, time, setFilter } = useTimeFilter({
    paramPrefix: `${DASHBOARD_KEY}_`,
  });
  const { selected: dimensions, setDimension, clearAll } = useDimensionFilters({
    dimensions: DIMENSION_KEYS, paramPrefix: `${DASHBOARD_KEY}_dim_`,
  });
  const [standard, setStandard] = useState(getStoredStandard);

  // Site focus set by clicking a row in the site-comparison table. Page-local:
  // it narrows this dashboard without re-issuing the session's active site.
  const [focusSiteId, setFocusSiteId] = useState(null);

  // Remount token for Refresh. Remounting the grid re-runs every widget's fetch
  // — simpler and more predictable than threading a nonce through each one, at
  // the cost of resetting per-chart view state, which is what Refresh implies.
  const [refreshKey, setRefreshKey] = useState(0);

  const [showNotes, setShowNotes] = useState(false);

  const CATALOG = React.useMemo(() => buildSafetyCommonCatalog(), []);
  const DEFAULT_LAYOUT = React.useMemo(() => safetyCommonDefaultLayout(), []);

  const activeDimensionCount = Object.keys(dimensions).length;
  const filtersDirty = preset !== 'last_30_days' || activeDimensionCount > 0 || !!focusSiteId;

  const resetFilters = useCallback(() => {
    setFilter({ preset: 'last_30_days' });
    clearAll();
    setFocusSiteId(null);
  }, [setFilter, clearAll]);

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Safety Dashboard</h1>
            <p className="mt-1 max-w-2xl text-gray-600">
              Safety performance, risk and corrective-action insights across your
              organization.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <LastUpdated />
              <button
                type="button"
                onClick={() => setShowNotes((v) => !v)}
                aria-expanded={showNotes}
                className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
              >
                <Info className="h-3.5 w-3.5" />
                What this dashboard does not cover
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setRefreshKey((k) => k + 1)}
              title="Reload every panel"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button variant="outline" onClick={() => navigate('/safety')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Safety Hub
            </Button>
          </div>
        </div>

        {showNotes && (
          <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 text-sm shadow-sm">
            <h2 className="mb-2 text-sm font-semibold text-gray-800">
              Known data limitations
            </h2>
            <ul className="list-disc space-y-1.5 pl-5 text-[13px] leading-relaxed text-gray-600">
              {DATA_NOTES.map((note) => <li key={note}>{note}</li>)}
            </ul>
          </div>
        )}

        <CustomizableDashboard
          key={refreshKey}
          dashboardKey={DASHBOARD_KEY}
          module="safety"
          // The site-comparison focus wins over the globally selected site, so
          // clicking a plant row narrows every panel on this page only.
          siteId={focusSiteId || activeSiteId}
          time={time}
          dimensions={dimensions}
          compare
          standard={standard}
          catalog={CATALOG}
          defaultLayout={DEFAULT_LAYOUT}
          extraContext={{ focusSiteId, onFocusSite: setFocusSiteId }}
          filterBar={(
            <FilterBar
              preset={preset}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onChange={setFilter}
              extra={(
                <>
                  <DimensionFilters
                    module="safety"
                    dims={SAFETY_COMMON_DIMENSIONS}
                    selected={dimensions}
                    onChange={setDimension}
                    onClearAll={clearAll}
                  />
                  <button
                    type="button"
                    onClick={resetFilters}
                    disabled={!filtersDirty}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white
                               px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                    title="Clear the period, all dimension filters and any site focus"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Reset Filters
                  </button>
                </>
              )}
            />
          )}
          actions={(
            <>
              <SavedFilters
                module="safety"
                current={{ time, dimensions }}
                onApply={(f) => {
                  const t = f?.time;
                  if (t?.preset) {
                    setFilter({ preset: t.preset, dateFrom: t.date_from, dateTo: t.date_to });
                  }
                  if (f?.dimensions) {
                    DIMENSION_KEYS.forEach((k) => setDimension(k, f.dimensions[k] || []));
                  }
                }}
              />
              <StandardToggle value={standard} onChange={setStandard} />
              <ExportMenu module="safety" specBuilder={safetyCommonExportSpec} />
            </>
          )}
        />
      </div>
    </div>
  );
};

export default SafetyCommonDashboard;
